import { ReactNode } from "react";

import { Experiment, getExperimentWithExposures } from "@/utils/experiment";

import { ConfirmationPageContext } from "../../hooks/use-confirmation-page";
import {
    Metadata,
    RegisteredMetadata
} from "../../hooks/use-registered-metadata";

import { ScreenList } from "./jira-screen-dictionary";

export const RESOLVE_TENANT_LOADER = "resolveTenantLoader" as const;
export const TEAM_CHOICE = "teamChoice" as const;
export const TEMPLATE_CHOICE = "templateChoice" as const;
export const PROVISIONING = "provisioning" as const;
export const MADLIB_PROJECT_SETUP = "madlibProjectSetup" as const;
export const PROVISIONING_WITH_WIZARD = "provisioningWithWizard" as const;
export const JTBD = "jobsToBeDone" as const;
export const PROJECT_LANDING_SELECTOR = "projectLandingSelector" as const;
export const WORK_ON_FIRST = "workOnFirst" as const;
export const TWC_PAGE_SWITCHER = "twcPageSwitcher" as const;
export const JIRA_FFL_PROJECT_SETUP = "jiraFflProjectSetup" as const;

export type ProvisioningScreenKey =
    | typeof RESOLVE_TENANT_LOADER
    | typeof TEAM_CHOICE
    | typeof TEMPLATE_CHOICE
    | typeof JTBD
    | typeof PROVISIONING
    | typeof PROVISIONING_WITH_WIZARD
    | typeof PROJECT_LANDING_SELECTOR
    | typeof MADLIB_PROJECT_SETUP
    | typeof WORK_ON_FIRST
    | typeof TWC_PAGE_SWITCHER
    | typeof JIRA_FFL_PROJECT_SETUP;

export type Conditional<T> = {
    default: T;
    conditionalOR?: Condition<T>[];
};
export type Condition<T> = {
    conditionalAND: ConditionPair<T>[];
};
export type ConditionPair<T> =
    | ExperimentConditionPair<T>
    | MetadataConditionPair<T>;

export type ExperimentConditionPair<T> = {
    experiment?: Experiment<T>;
    value?: T;
    conditionalOR?: Condition<T>[];
    type: "experiment";
};

export type MetadataConditionPair<T> = {
    key?: string;
    value?: T;
    conditionalOR?: Condition<T>[];
    type?: "metadata" | undefined;
};

export type Config = {
    onMount: (confirmationPageContext: ConfirmationPageContext) => void;
    position: Conditional<number>;
    visibility?: Conditional<Metadata>;
};
export type ScreenConfig = Partial<Record<ProvisioningScreenKey, Config>>;
export type ProvisioningScreenDictionary = Partial<
    Record<ProvisioningScreenKey, ReactNode>
>;

export type ScreenListProps<T> = {
    screensConfig: ScreenConfig;
    fullScreenList: T;
};

export function getConfiguredScreenList({
    screensConfig,
    fullScreenList
}: ScreenListProps<ScreenList>): ScreenList {
    return (
        fullScreenList
            // filter on screens that exist in config.
            .filter((screen) => screensConfig[screen])
            // sort screens based on the explicit position.
            .sort((screenA, screenB) => {
                const configA =
                    screensConfig[screenA] ??
                    ({ position: { default: 150 } } as Config);
                const configB =
                    screensConfig[screenB] ??
                    ({ position: { default: 150 } } as Config);

                return configA.position.default - configB.position.default;
            })
    );
}

export function getVisibility(
    metadata: RegisteredMetadata,
    visibility: Conditional<Metadata> | undefined
) {
    // if no visbility conditions are set, show the screen by default
    if (visibility === null || visibility === undefined) {
        return true;
    }

    // if visibility is set to true by default, show the screen
    if (visibility.default) {
        return true;
    }

    const { conditionalOR = [] } = visibility;

    return evaluateConditionsInline(metadata, conditionalOR);
}

export function evaluateConditionsInline(
    metadata: RegisteredMetadata,
    conditions: Condition<Metadata>[]
): boolean {
    if (conditions === undefined) {
        return false;
    }

    const reducedOrResults = conditions.map((OR) => {
        // reset for each OR branch so one failing branch does not short-circuit others
        let conditionsPass = true;

        return (
            OR.conditionalAND
                // eslint-disable-next-line complexity
                .map((condition) => {
                    // fail early if condition already fails
                    if (!conditionsPass) {
                        return false;
                    }

                    if (condition.type === "experiment") {
                        const { experiment, value, conditionalOR } = condition;

                        // if key is undefined, set pass to true. ConditionalOr will be evaluated instead.
                        let pass = experiment === undefined;

                        if (experiment) {
                            const data = getExperimentWithExposures(experiment);
                            if (
                                data.type === "enrolled" ||
                                data.type === "notEnrolled"
                            ) {
                                pass = data.value === value;
                            }
                        }

                        conditionsPass = conditionsPass && pass === true;

                        // fail early if condition already fails
                        if (!conditionsPass) {
                            return false;
                        }

                        if (conditionalOR) {
                            pass =
                                pass &&
                                evaluateConditionsInline(
                                    metadata,
                                    conditionalOR
                                );
                        }

                        return pass;
                    } else if (
                        condition.type === "metadata" ||
                        condition.type === undefined
                    ) {
                        const { key, value, conditionalOR } = condition;

                        const metadataValue = metadata?.get(key ?? "");

                        // if key is undefined, set pass to true. ConditionalOr will be evaluated instead.
                        let pass = key === undefined;

                        // check that each value is in metadata
                        if (
                            Array.isArray(value) &&
                            Array.isArray(metadataValue)
                        ) {
                            pass =
                                value.filter(
                                    (val = false) =>
                                        metadataValue.filter(
                                            (meta) => meta === val
                                        ).length > 0
                                ).length === value.length;
                        } else {
                            // evaluate condition against stored metadata
                            pass = metadataValue === value;
                        }

                        conditionsPass = conditionsPass && pass === true;

                        // fail early if condition already fails
                        if (!conditionsPass) {
                            return false;
                        }

                        if (conditionalOR) {
                            pass =
                                pass &&
                                evaluateConditionsInline(
                                    metadata,
                                    conditionalOR
                                );
                        }

                        return pass;
                    }
                })
                .reduce(
                    // consolidate conditions in AND clause to one boolean value.
                    (conditionOne, conditionTwo) => conditionOne && conditionTwo
                )
        );
    });

    // at least one OR condition should be true to show the screen
    return reducedOrResults.filter((res) => res === true).length > 0;
}
